/**
 * ============================================================================
 * CustomerInsightsNew - PRODUCTION-READY CUSTOMER INSIGHTS PAGE
 * ============================================================================
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { useConfirmation } from '../contexts/ConfirmationContext';
import { useVenueRole } from '../hooks/useVenueRole';
import { AlertCircle, RefreshCw, Smartphone, UserPlus, CreditCard, CalendarDays, ShieldCheck } from 'lucide-react';
import { useCustomerInsights } from '../hooks/useCustomerInsights';
import { CustomerStats } from '../components/customerInsights/CustomerStats';
import { CustomerTable } from '../components/customerInsights/CustomerTable';
import { CustomerGrowthChart } from '../components/customerInsights/CustomerGrowthChart';
import { CustomerTypeChart } from '../components/customerInsights/CustomerTypeChart';
import { PeakHoursChart } from '../components/customerInsights/PeakHoursChart';
import { SportPreferencesChart } from '../components/customerInsights/SportPreferencesChart';
import { WeeklyPerformanceChart } from '../components/customerInsights/WeeklyPerformanceChart';
import { RetentionRing, StatusDonut, MemberTrendChart } from '../components/customerInsights/MembershipInsights';
import CustomerProfileModal from '../components/modals/CustomerProfileModal';
import { vendorApi } from '../services/api';
import type { PeriodFilter, CustomerInsightsFilters, SourceFilter, Customer } from '../types/customerInsights';

export interface TrustedEntry {
  id: number;
  user_id: number;
  name: string;
  email: string;
  phone: string | null;
  payment_deadline_hours: number;
  max_active_reservations: number;
  notes: string | null;
  is_active: boolean;
  added_by_name: string;
  created_at: string;
}

export interface TrustSettings {
  payment_deadline_hours: number;
  max_active_reservations: number;
  notes: string;
}

// ============================================================================
// MAIN PAGE
// ============================================================================

export default function CustomerInsightsNew() {
  const { user } = useAuth();
  const venue = user?.venues?.[0];
  const queryClient = useQueryClient();
  const { alert } = useConfirmation();
  const { can } = useVenueRole();

  // ============================================================================
  // STATE
  // ============================================================================

  const [period, setPeriod] = useState<PeriodFilter>('month');
  const [source, setSource] = useState<SourceFilter>('all');
  const [filters, setFilters] = useState<CustomerInsightsFilters>({
    search: '',
    segment: 'all',
    type: 'all',
  });

  // Customer profile modal
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  const { data, isPending, error, refetch } = useCustomerInsights(
    venue?.id,
    period,
    filters,
    source
  );

  // Trusted customers list — trust management is a bookings.edit feature, so
  // staff without it see insights without the trusted-reservation overlay
  // (rather than a background 403 on this page).
  const canManageTrust = can('bookings.edit');
  const { data: trustedList = [] } = useQuery<TrustedEntry[]>({
    queryKey: ['trustedCustomers', venue?.id],
    queryFn: () => vendorApi.getTrustedCustomers(venue!.id),
    enabled: !!venue?.id && canManageTrust,
    staleTime: 60_000,
  });

  // Build user_id-keyed lookup (active entries only) — more reliable than email matching
  const trustedByUserId = useMemo(() => {
    const map = new Map<number, TrustedEntry>();
    trustedList.filter(e => e.is_active).forEach(e => map.set(e.user_id, e));
    return map;
  }, [trustedList]);

  // Add trusted mutation
  const addTrustMutation = useMutation({
    mutationFn: ({ userId, settings }: { userId: number; settings: TrustSettings }) =>
      vendorApi.addTrustedCustomer(venue!.id, {
        user_id: userId,
        payment_deadline_hours: settings.payment_deadline_hours,
        max_active_reservations: settings.max_active_reservations,
        notes: settings.notes || undefined,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trustedCustomers', venue?.id] }),
    onError: (error) => {
      alert({
        title: 'Failed to Add Trusted Status',
        message: error instanceof Error ? error.message : 'Something went wrong. Please try again.',
        variant: 'danger',
      });
    },
  });

  // Update trusted mutation
  const updateTrustMutation = useMutation({
    mutationFn: ({ entryId, settings }: { entryId: number; settings: TrustSettings }) =>
      vendorApi.updateTrustedCustomer(entryId, {
        payment_deadline_hours: settings.payment_deadline_hours,
        max_active_reservations: settings.max_active_reservations,
        notes: settings.notes || undefined,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trustedCustomers', venue?.id] }),
    onError: (error) => {
      alert({
        title: 'Failed to Update Trusted Settings',
        message: error instanceof Error ? error.message : 'Something went wrong. Please try again.',
        variant: 'danger',
      });
    },
  });

  // Remove trusted mutation
  const removeTrustMutation = useMutation({
    mutationFn: (entryId: number) => vendorApi.removeTrustedCustomer(entryId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['trustedCustomers', venue?.id] }),
    onError: (error) => {
      alert({
        title: 'Failed to Remove Trusted Status',
        message: error instanceof Error ? error.message : 'Something went wrong. Please try again.',
        variant: 'danger',
      });
    },
  });

  // ============================================================================
  // HANDLERS
  // ============================================================================

  // Reactively derive userId and trustedEntry from selected customer
  // These update automatically after trust mutations invalidate the query
  const selectedUserId = useMemo(() => {
    if (!selectedCustomer || selectedCustomer.source !== 'spotts') return null;
    const parsed = parseInt(selectedCustomer.id.replace('spotts_', ''), 10);
    return isNaN(parsed) ? null : parsed;
  }, [selectedCustomer]);

  const selectedTrustedEntry = useMemo(() => {
    if (!selectedUserId) return null;
    return trustedByUserId.get(selectedUserId) ?? null;
  }, [selectedUserId, trustedByUserId]);

  const handleFilterChange = (newFilters: Partial<CustomerInsightsFilters>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleCustomerClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsProfileModalOpen(true);
  };

  const handleCloseProfileModal = () => {
    setIsProfileModalOpen(false);
    setSelectedCustomer(null);
  };

  // Hover prefetch
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const handleCustomerHover = useCallback((customer: Customer) => {
    if (!venue?.id) return;
    clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => {
      queryClient.prefetchQuery({
        queryKey: ['customerProfile', venue.id, customer.name, customer.email, customer.source],
        queryFn: () => vendorApi.getCustomerProfile(venue.id, {
          name: customer.name,
          email: customer.email || undefined,
          phone: customer.phone || undefined,
          source: customer.source,
        }),
        staleTime: 5 * 60 * 1000,
      });
    }, 100);
  }, [venue?.id, queryClient]);

  // Cleanup timers
  useEffect(() => {
    return () => {
      clearTimeout(hoverTimerRef.current);
    };
  }, []);

  // ============================================================================
  // LOADING / ERROR / NO VENUE
  // ============================================================================

  if (isPending) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading customer insights...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">Error Loading Insights</h2>
        <p className="text-gray-600 mb-4">
          {error instanceof Error ? error.message : 'An error occurred'}
        </p>
        <button onClick={() => refetch()} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors">
          Retry
        </button>
      </div>
    );
  }

  if (!venue) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <AlertCircle className="w-12 h-12 text-gray-400 mb-4" />
        <h2 className="text-xl font-bold text-gray-900 mb-2">No Venue Found</h2>
        <p className="text-gray-600">Please ensure you have a venue associated with your account.</p>
      </div>
    );
  }

  const activeTrustedCount = trustedList.filter(e => e.is_active).length;

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
          <div>
            <h1 className="text-3xl font-black text-gray-900 mb-1">Customers</h1>
            <p className="text-gray-600 text-sm">Understand your customer behaviour and manage trusted reservations</p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1.5 bg-blue-600 text-white rounded-xl px-3 py-1.5 shadow-sm">
              <CalendarDays className="w-3.5 h-3.5" />
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value as PeriodFilter)}
                className="bg-transparent text-xs font-semibold outline-none cursor-pointer appearance-none pr-1"
                aria-label="Select time period"
              >
                <option value="week" className="text-gray-900 bg-white">This Week</option>
                <option value="month" className="text-gray-900 bg-white">This Month</option>
                <option value="quarter" className="text-gray-900 bg-white">This Quarter</option>
                <option value="year" className="text-gray-900 bg-white">This Year</option>
              </select>
            </div>

            <div className="flex items-center gap-1.5 bg-white rounded-xl border border-gray-200 p-1.5 shadow-sm">
              <button onClick={() => setSource('all')} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all ${source === 'all' ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>All</button>
              <button onClick={() => setSource('spotts')} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 ${source === 'spotts' ? 'bg-blue-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
                <Smartphone className="w-3 h-3" />Spotts
              </button>
              <button onClick={() => setSource('manual')} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 ${source === 'manual' ? 'bg-green-600 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`}>
                <UserPlus className="w-3 h-3" />Manual
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <CustomerStats
        total_customers={data?.insights.metrics.total_customers || 0}
        new_customers={data?.insights.metrics.new_customers || 0}
        returning_customers={data?.insights.metrics.returning_customers || 0}
        avg_bookings={data?.insights.metrics.avg_bookings || 0}
        return_rate={data?.insights.metrics.return_rate || 0}
      />

      <CustomerGrowthChart data={data?.insights.customer_growth || []} />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <CustomerTypeChart data={data?.insights.customer_types || []} />
        <PeakHoursChart data={data?.insights.peak_hours || []} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SportPreferencesChart data={data?.insights.sport_preferences || []} />
        <WeeklyPerformanceChart data={data?.insights.weekly_performance || []} />
      </div>

      {data?.insights.membership_insights && (
        <div className="space-y-4 sm:space-y-6">
          <div className="flex items-center gap-3 pt-2">
            <CreditCard className="w-5 h-5 text-purple-600" />
            <h2 className="text-xl font-bold text-gray-900">Membership Insights</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            <RetentionRing rate={data.insights.membership_insights.metrics.retention_rate} />
            <StatusDonut data={data.insights.membership_insights.status_breakdown} />
          </div>
          <MemberTrendChart data={data.insights.membership_insights.trend} />
        </div>
      )}

      {/* ================================================================ */}
      {/* CUSTOMER TABLE + TRUSTED MANAGEMENT                              */}
      {/* ================================================================ */}
      <div>
        {/* Section header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Customers
              {source !== 'all' && (
                <span className={`ml-2 text-sm font-medium ${source === 'spotts' ? 'text-blue-600' : 'text-green-600'}`}>
                  ({source === 'spotts' ? 'Spotts App' : 'Manual'})
                </span>
              )}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {data?.filteredCustomers?.length || 0} customers
              {activeTrustedCount > 0 && (
                <span className="ml-2 inline-flex items-center gap-1 text-green-600 font-medium">
                  <ShieldCheck className="w-3.5 h-3.5" />
                  {activeTrustedCount} trusted
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Customer Table — click a row to view/manage trust in the profile modal */}
        <CustomerTable
          customers={data?.filteredCustomers || []}
          filters={filters}
          onFilterChange={handleFilterChange}
          onCustomerClick={handleCustomerClick}
          onCustomerHover={handleCustomerHover}
          trustedByUserId={trustedByUserId}
        />
      </div>

      {/* Customer Profile Modal — includes trust management for Spotts users */}
      {venue && (
        <CustomerProfileModal
          isOpen={isProfileModalOpen}
          onClose={handleCloseProfileModal}
          venueId={venue.id}
          customer={selectedCustomer ? {
            name: selectedCustomer.name,
            email: selectedCustomer.email,
            phone: selectedCustomer.phone,
            source: selectedCustomer.source as 'spotts' | 'manual',
          } : null}
          userId={selectedUserId}
          trustedEntry={selectedTrustedEntry}
          onAddTrust={(settings) => selectedUserId && addTrustMutation.mutate({ userId: selectedUserId, settings })}
          onUpdateTrust={(entryId, settings) => updateTrustMutation.mutate({ entryId, settings })}
          onRemoveTrust={(entryId) => removeTrustMutation.mutate(entryId)}
          isAddTrustPending={addTrustMutation.isPending}
          isUpdateTrustPending={updateTrustMutation.isPending}
          isRemoveTrustPending={removeTrustMutation.isPending}
        />
      )}
    </div>
  );
}
